import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

@Injectable({ providedIn: 'root' })
export class IdentificationTitleService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get apiUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/countries`;
  }

  private normalizeStatus(status: string | undefined): 'active' | 'inactive' {
    if (!status) return 'active';
    return status.toLowerCase() as 'active' | 'inactive';
  }

  /**
   * LISTA: Identification titles (server-side pagination)
   */
  getIdentificationTitles(
    countryId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<IdentificationTitlePageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim().toUpperCase());
    }

    const url = `${this.apiUrl}/${countryId}/identifications`;
    return this.httpClient.get<ApiResponse<PageableData>>(url, { params }).pipe(
      map((response) => {
        const pageableData = response.data;
        const items = pageableData?.content || [];
        return {
          items: items.map(
            (it: any) =>
              ({
                id: it.id,
                identificationId: it.identificationId || '',
                name: it.name || '',
                code: it.code || '',
                typeFormatId: it.typeFormatId || '',
                typeFormatName: it.typeFormatName || '',
                min: it.min ?? '',
                max: it.max ?? '',
                format: it.format || '',
                mask: it.mask || '',
                procedureNumber:
                  it.procedure_number ?? it.procedureNumber ?? false,
                province: it.province ?? false,
                validation: it.validation ?? false,
                status: this.normalizeStatus(it.status),
                createdAt: it.createdAt || '',
                updatedAt: it.updatedAt || '',
              }) as IdentificationTitle,
          ),
          page: pageableData?.page ?? 0,
          size: pageableData?.size ?? size,
          totalElements: pageableData?.totalElements ?? 0,
          totalPages: pageableData?.totalPages ?? 0,
        } as IdentificationTitlePageResponse;
      }),
    );
  }

  getIdentificationTitleById(countryId: string, id: string | number) {
    const url = `${this.apiUrl}/${countryId}/identifications/${id}`;
    return this.httpClient.get<ApiResponse<any>>(url).pipe(
      map((resp) => this.mapIdentificationTitle(resp.data)),
    );
  }

  /**
   * Detalle por identificationId de catálogo (tipo de documento).
   * GET …/identifications/{identificationId}
   * El id del select es el de la fila del país.
   */
  getIdentificationTitleByIdentificationId(
    countryId: string,
    identificationId: string | number,
  ) {
    const url = `${this.apiUrl}/${countryId}/identifications/${identificationId}`;
    return this.httpClient.get<ApiResponse<any>>(url).pipe(
      map((resp) => this.mapIdentificationTitle(resp.data)),
    );
  }

  private mapIdentificationTitle(it: any): IdentificationTitle {
    return {
      id: it?.id || '',
      identificationId: it?.identificationId || '',
      name: it?.name || '',
      code: it?.code || '',
      typeFormatId: it?.typeFormatId || '',
      typeFormatName: it?.typeFormatName || '',
      min: it?.min ?? '',
      max: it?.max ?? '',
      format: it?.format || '',
      mask: it?.mask || '',
      procedureNumber:
        it?.procedure_number ?? it?.procedureNumber ?? false,
      province: it?.province ?? false,
      validation: it?.validation ?? false,
      status: this.normalizeStatus(it?.status),
      createdAt: it?.createdAt || '',
      updatedAt: it?.updatedAt || '',
    };
  }

  createIdentificationTitle(countryId: string, payload: any) {
    const url = `${this.apiUrl}/${countryId}/identifications`;
    return this.httpClient
      .post<{ success: boolean; message: string }>(url, payload)
      .pipe(
        map((res) => ({
          message: res.message || 'Identificación creada',
        })),
      );
  }

  updateIdentificationTitle(
    countryId: string,
    id: string | number,
    payload: any,
  ) {
    const url = `${this.apiUrl}/${countryId}/identifications/${id}`;
    return this.httpClient
      .put<{ success: boolean; message: string }>(url, payload)
      .pipe(
        map((res) => ({
          message: res.message || 'Identificación actualizada',
        })),
      );
  }

  deleteIdentificationTitle(countryId: string, id: string | number) {
    const url = `${this.apiUrl}/${countryId}/identifications/${id}`;
    return this.httpClient
      .delete<{ success: boolean; message: string }>(url)
      .pipe(
        map((res) => ({
          message: res.message || 'Identificación eliminada',
        })),
      );
  }
}

export interface IdentificationTitle {
  id: string;
  identificationId: string;
  name: string;
  code: string;
  typeFormatId: string;
  typeFormatName: string;
  min: string | number;
  max: string | number;
  format: string;
  mask: string;
  /** UI camelCase; API field: procedure_number */
  procedureNumber: boolean;
  province: boolean;
  validation: boolean;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface IdentificationTitlePageResponse {
  items: IdentificationTitle[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}
